import axios from './axiosInstance';
import { APIFormFolder } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

/**
 * The resort's form folders: system + custom, with archived and hidden ones
 * flagged rather than left out (a stored value still has to resolve a name).
 * Authorised by `formFolders:read` OR `forms:read`.
 */
const getFormFolders = ({ sport, areaId }: SportAreaParams): Promise<APIFormFolder[]> => {
  const url = `/${sport}/areas/${areaId}/formfolders`;
  return axios.get(url)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

interface FormFolderFields {
  name?: string;
  /** `null` clears it on an update. */
  description?: string | null;
}

interface CreateFormFolderParams extends SportAreaParams, FormFolderFields {
  name: string;
}

interface UpdateFormFolderParams extends SportAreaParams, FormFolderFields {
  folderId: string;
}

interface ArchiveFormFolderParams extends SportAreaParams {
  folderId: string;
}

// A folder id carries a colon (`folder:<uuid>`); the API decodes either spelling.
const folderUrl = ({ sport, areaId, folderId }: SportAreaParams & { folderId: string }) => (
  `/${sport}/areas/${areaId}/formfolders/${encodeURIComponent(folderId)}`
);

/**
 * Create a custom folder. A name that already belongs to an active folder
 * returns THAT folder (as tags do), so callers that mean "new" check first.
 * 402 without the `gis` module; 409 when the name belongs to an archived
 * folder; 400 for a built-in folder's name. Errors are thrown as they come:
 * the caller maps them (see formFoldersPageLogic).
 */
const createFormFolder = ({ sport, areaId, name, description }: CreateFormFolderParams): Promise<APIFormFolder> => {
  const url = `/${sport}/areas/${areaId}/formfolders`;
  return axios.put(url, { name, description })
    .then((response) => response.data);
};

/** Rename, re-describe or re-file a custom folder. Only the fields passed change. */
const updateFormFolder = ({ sport, areaId, folderId, name, description }: UpdateFormFolderParams): Promise<APIFormFolder> => {
  return axios.post(folderUrl({ sport, areaId, folderId }), { name, description })
    .then((response) => response.data);
};

/**
 * Archive a custom folder (there is no delete, and no un-archive). 409 with
 * `{ message, formCount }` while forms are still in it.
 */
const archiveFormFolder = ({ sport, areaId, folderId }: ArchiveFormFolderParams): Promise<APIFormFolder> => {
  return axios.delete(folderUrl({ sport, areaId, folderId }))
    .then((response) => response.data);
};

const formFolderFunctions = {
  getFormFolders,
  createFormFolder,
  updateFormFolder,
  archiveFormFolder,
};

export default formFolderFunctions;
